import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { KEY_TERMS, DEFAULT_PRICING_TABLE } from '../data/contractTemplate';
import {
  BRAND,
  BRAND_DARK,
  BRAND_TINT,
  PANEL_BG,
  TEXT_DARK,
  loadUploadedImageAsDataUrl,
  loadLogoAsDataUrl,
  getImageDimensions,
  getImageFormat,
  formatDate,
  formatMoney,
} from './pdfBrand';

const PAGE_MARGIN = 14;
const FOOTER_SPACE = 18;
const FONT_SIZE = 8.5;
const LINE_HEIGHT = 3.9;
const BLANK = '________________';

// The contract is laid out to fit on two A4 pages: parties, campaign
// details and pricing as compact tables on page 1, the condensed key terms
// and signatures after them.
class PdfCursor {
  constructor(doc) {
    this.doc = doc;
    this.pageWidth = doc.internal.pageSize.getWidth();
    this.pageHeight = doc.internal.pageSize.getHeight();
    this.contentWidth = this.pageWidth - PAGE_MARGIN * 2;
    this.y = PAGE_MARGIN;
  }

  ensureSpace(height) {
    if (this.y + height > this.pageHeight - FOOTER_SPACE) {
      this.doc.addPage();
      this.y = PAGE_MARGIN;
    }
  }

  gap(height = LINE_HEIGHT) {
    this.y += height;
  }

  // Slim brand-color band with white text marking each section.
  heading(title) {
    const bandHeight = 6;
    this.ensureSpace(bandHeight + 10);
    this.doc.setFillColor(...BRAND);
    this.doc.rect(PAGE_MARGIN, this.y, this.contentWidth, bandHeight, 'F');
    this.doc.setTextColor(255, 255, 255);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setFontSize(9.5);
    this.doc.text(title, PAGE_MARGIN + 2.5, this.y + bandHeight - 1.8);
    this.y += bandHeight + 4;
    this.doc.setTextColor(...TEXT_DARK);
    this.doc.setFont('helvetica', 'normal');
    this.doc.setFontSize(FONT_SIZE);
  }

  paragraph(text, { bold = false } = {}) {
    this.doc.setFont('helvetica', bold ? 'bold' : 'normal');
    this.doc.setFontSize(FONT_SIZE);
    this.doc.setTextColor(...TEXT_DARK);
    const lines = this.doc.splitTextToSize(text, this.contentWidth);
    this.ensureSpace(lines.length * LINE_HEIGHT);
    this.doc.text(lines, PAGE_MARGIN, this.y);
    this.y += lines.length * LINE_HEIGHT + 1;
    this.doc.setFont('helvetica', 'normal');
  }

  // "n. Title: text" - numbered clause with a bold brand-colored title run
  // in on the first line. The first line is narrower (the title occupies
  // its start), so words are wrapped manually.
  term(number, title, text) {
    const indent = 5;
    const width = this.contentWidth - indent;
    const titleText = `${title}:`;

    this.doc.setFontSize(FONT_SIZE);
    this.doc.setFont('helvetica', 'bold');
    const titleWidth = this.doc.getTextWidth(titleText) + 1.2;
    this.doc.setFont('helvetica', 'normal');

    const lines = [];
    let current = '';
    let maxWidth = width - titleWidth;
    for (const word of text.split(/\s+/)) {
      const candidate = current ? `${current} ${word}` : word;
      if (current && this.doc.getTextWidth(candidate) > maxWidth) {
        lines.push(current);
        current = word;
        maxWidth = width;
      } else {
        current = candidate;
      }
    }
    if (current) lines.push(current);

    this.ensureSpace(lines.length * LINE_HEIGHT);
    const x = PAGE_MARGIN + indent;
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(...BRAND_DARK);
    this.doc.text(`${number}.`, PAGE_MARGIN, this.y);
    this.doc.text(titleText, x, this.y);
    this.doc.setFont('helvetica', 'normal');
    this.doc.setTextColor(...TEXT_DARK);
    lines.forEach((line, index) => {
      this.doc.text(line, index === 0 ? x + titleWidth : x, this.y);
      this.y += LINE_HEIGHT;
    });
    this.y += 1.2;
  }
}

const valueOr = (value) => (value !== null && value !== undefined && value !== '' ? String(value) : BLANK);

// Shared look for the label/value tables (parties, campaign details).
const LABEL_STYLE = { fontStyle: 'bold', textColor: BRAND_DARK, fillColor: PANEL_BG, cellWidth: 34 };
const kvTable = (doc, startY, head, body) => {
  autoTable(doc, {
    startY,
    margin: { left: PAGE_MARGIN, right: PAGE_MARGIN },
    head: head ? [head] : undefined,
    body,
    theme: 'grid',
    styles: { fontSize: FONT_SIZE, cellPadding: 1.4, lineColor: [230, 222, 212], lineWidth: 0.2, textColor: TEXT_DARK },
    headStyles: { fillColor: BRAND, textColor: [255, 255, 255], fontStyle: 'bold' },
    columnStyles: { 0: LABEL_STYLE, 2: LABEL_STYLE },
  });
  return doc.lastAutoTable.finalY;
};

/**
 * Generates the LED Digital Billboard Advertising Contract as a (two-page)
 * PDF and triggers a browser download. `contract` is a contract record as
 * returned by the backend (contractAPI.get/list); signature/logo images are
 * fetched and embedded on a best-effort basis - a missing/unreachable image
 * never blocks the rest of the document from generating.
 */
export const generateContractPdf = async (contract) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const cursor = new PdfCursor(doc);

  const [logoDataUrl, providerSigDataUrl, clientSigDataUrl] = await Promise.all([
    loadLogoAsDataUrl(),
    loadUploadedImageAsDataUrl(contract.provider_signature_url),
    loadUploadedImageAsDataUrl(contract.client_signature_url),
  ]);

  // ── Header: logo left, title right, brand rule underneath ────────────
  const headerHeight = 22;

  if (logoDataUrl) {
    const dims = await getImageDimensions(logoDataUrl);
    if (dims) {
      const logoHeight = 15;
      const logoWidth = (dims.width / dims.height) * logoHeight;
      doc.addImage(logoDataUrl, getImageFormat(logoDataUrl), PAGE_MARGIN, (headerHeight - logoHeight) / 2, logoWidth, logoHeight);
    }
  }

  doc.setTextColor(...BRAND_DARK);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  const titleLines = doc.splitTextToSize('LED DIGITAL BILLBOARD ADVERTISING CONTRACT AGREEMENT', cursor.contentWidth - 50);
  let titleY = 10;
  titleLines.forEach((line) => {
    doc.text(line, cursor.pageWidth - PAGE_MARGIN, titleY, { align: 'right' });
    titleY += 5;
  });

  doc.setDrawColor(...BRAND);
  doc.setLineWidth(0.8);
  doc.line(0, headerHeight, cursor.pageWidth, headerHeight);

  // Agreement No. (left) / Date (right)
  cursor.y = headerHeight + 6;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND_DARK);
  doc.text('Agreement No.: ', PAGE_MARGIN, cursor.y);
  const agreementLabelWidth = doc.getTextWidth('Agreement No.: ');
  const dateText = valueOr(formatDate(contract.contract_date));
  doc.setFont('helvetica', 'normal');
  const dateWidth = doc.getTextWidth(dateText);
  doc.setTextColor(...TEXT_DARK);
  doc.text(valueOr(contract.agreement_no), PAGE_MARGIN + agreementLabelWidth, cursor.y);
  doc.text(dateText, cursor.pageWidth - PAGE_MARGIN, cursor.y, { align: 'right' });
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND_DARK);
  doc.text('Date: ', cursor.pageWidth - PAGE_MARGIN - dateWidth, cursor.y, { align: 'right' });
  cursor.gap(5);

  cursor.paragraph(
    'This LED Digital Billboard Advertising Contract ("Agreement") is entered into between the Parties below. ' +
      'The Provider is referred to as "CloudScreen" and the Advertiser/Client as the "Advertiser".'
  );
  cursor.gap(1);

  // ── 1. Parties (side by side) ────────────────────────────────────────
  cursor.heading('1. PARTIES');
  cursor.y = kvTable(doc, cursor.y - 2, [
    { content: 'SERVICE PROVIDER ("CloudScreen")', colSpan: 2 },
    { content: 'ADVERTISER / CLIENT', colSpan: 2 },
  ], [
    ['Company', valueOr(contract.provider_company_name), 'Company', valueOr(contract.client_company_name)],
    ['Address', valueOr(contract.provider_address), 'Address', valueOr(contract.client_address)],
    ['Phone / WhatsApp', valueOr(contract.provider_phone), 'Phone', valueOr(contract.client_phone)],
    ['Email', valueOr(contract.provider_email), 'Email', valueOr(contract.client_email)],
    ['', '', 'Contact Person', valueOr(contract.client_contact_person)],
    ['', '', 'Tax ID', contract.client_tax_id || '-'],
  ]) + 5;

  // ── 2. Campaign details ──────────────────────────────────────────────
  cursor.heading('2. CAMPAIGN DETAILS');
  cursor.y = kvTable(doc, cursor.y - 2, null, [
    ['Advertising Location', valueOr(contract.advertising_location), 'LED Screen Location', valueOr(contract.led_screen_location)],
    ['Screen Size / Res.', valueOr(contract.screen_resolution), 'Package', valueOr(contract.package_name)],
    ['Start Date', valueOr(formatDate(contract.campaign_start_date)), 'End Date', valueOr(formatDate(contract.campaign_end_date))],
    ['Time Slot', valueOr(contract.time_slot), 'Max Ad Duration', contract.max_ad_duration_seconds ? `${contract.max_ad_duration_seconds} seconds / play` : BLANK],
    ['Total Duration', valueOr(contract.total_campaign_duration), 'Playback / Rotation', valueOr(contract.rotation_notes)],
    ['Special Instructions', { content: contract.special_instructions || '-', colSpan: 3 }],
  ]) + 5;

  // ── 3. Pricing (left) and payment summary (right) ────────────────────
  cursor.heading('3. PRICING & PAYMENT');
  const halfWidth = (cursor.contentWidth - 6) / 2;
  const tableTop = cursor.y - 2;

  const pricingRows = (contract.pricing_table && contract.pricing_table.length ? contract.pricing_table : DEFAULT_PRICING_TABLE)
    .map((row) => [row.timeSlot, `THB ${formatMoney(row.price)}`]);

  autoTable(doc, {
    startY: tableTop,
    margin: { left: PAGE_MARGIN, right: PAGE_MARGIN + halfWidth + 6 },
    head: [['Time Slot', 'Package Price']],
    body: pricingRows,
    theme: 'grid',
    styles: { fontSize: FONT_SIZE, cellPadding: 1.4, lineColor: [230, 222, 212], lineWidth: 0.2, textColor: TEXT_DARK },
    headStyles: { fillColor: BRAND, textColor: [255, 255, 255] },
    alternateRowStyles: { fillColor: BRAND_TINT },
    columnStyles: { 1: { halign: 'right', fontStyle: 'bold' } },
  });
  const pricingEnd = doc.lastAutoTable.finalY;

  const emphasis = { fontStyle: 'bold', fillColor: BRAND_TINT };
  autoTable(doc, {
    startY: tableTop,
    margin: { left: PAGE_MARGIN + halfWidth + 6, right: PAGE_MARGIN },
    head: [['Payment Summary', 'Amount']],
    body: [
      ['Total Advertising Fee', `THB ${formatMoney(contract.total_advertising_fee)}`],
      ['VAT / Tax', `THB ${formatMoney(contract.vat_tax)}`],
      [{ content: 'Total Payable', styles: emphasis }, { content: `THB ${formatMoney(contract.total_payable)}`, styles: emphasis }],
      ['Payment Received', `THB ${formatMoney(contract.payment_received)}`],
      [{ content: 'Balance Due', styles: emphasis }, { content: `THB ${formatMoney(contract.balance)}`, styles: { ...emphasis, textColor: BRAND_DARK } }],
    ],
    theme: 'grid',
    styles: { fontSize: FONT_SIZE, cellPadding: 1.4, lineColor: [230, 222, 212], lineWidth: 0.2, textColor: TEXT_DARK },
    headStyles: { fillColor: BRAND, textColor: [255, 255, 255] },
    columnStyles: { 1: { halign: 'right' } },
  });
  cursor.y = Math.max(pricingEnd, doc.lastAutoTable.finalY) + 3;

  cursor.paragraph(
    'The applicable price is the one stated in the accepted quotation, invoice or booking confirmation. 100% payment is due before campaign commencement unless otherwise agreed in writing.',
    { bold: true }
  );
  cursor.gap(1);

  // ── 4. Key terms ─────────────────────────────────────────────────────
  cursor.heading('4. TERMS & CONDITIONS');
  KEY_TERMS.forEach((item, index) => cursor.term(index + 1, item.title, item.text));
  cursor.gap(2);

  // ── 5. Signatures ────────────────────────────────────────────────────
  const signatureBlockHeight = 46;
  cursor.ensureSpace(signatureBlockHeight + 14);
  cursor.heading('5. SIGNATURES');
  cursor.paragraph('By signing below, both Parties accept this Agreement and the campaign details stated above.');
  cursor.gap(3);

  const colGap = 6;
  const panelWidth = (cursor.contentWidth - colGap) / 2;
  const leftX = PAGE_MARGIN + 3;
  const rightX = PAGE_MARGIN + panelWidth + colGap + 3;
  const blockTop = cursor.y;

  doc.setFillColor(...PANEL_BG);
  doc.roundedRect(PAGE_MARGIN, blockTop - 4, panelWidth, signatureBlockHeight, 2, 2, 'F');
  doc.roundedRect(PAGE_MARGIN + panelWidth + colGap, blockTop - 4, panelWidth, signatureBlockHeight, 2, 2, 'F');

  const renderSignatureColumn = (x, heading, companyValue, repName, repPosition, sigDataUrl, signedDate) => {
    const labelled = (label, value, y) => {
      doc.setFont('helvetica', 'bold');
      doc.text(`${label}:`, x, y);
      const labelWidth = doc.getTextWidth(`${label}:`) + 1.5;
      doc.setFont('helvetica', 'normal');
      doc.text(doc.splitTextToSize(value, panelWidth - 6 - labelWidth)[0], x + labelWidth, y);
      return labelWidth;
    };

    let y = blockTop;
    doc.setFontSize(FONT_SIZE);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BRAND_DARK);
    doc.text(heading, x, y);
    doc.setTextColor(...TEXT_DARK);
    y += 5.5;
    labelled('Company', valueOr(companyValue), y);
    y += 5;
    labelled('Name', valueOr(repName), y);
    y += 5;
    labelled('Position', valueOr(repPosition), y);
    y += 10;

    doc.setFont('helvetica', 'bold');
    doc.text('Signature:', x, y);
    const sigX = x + doc.getTextWidth('Signature:') + 1.5;
    let drewImage = false;
    if (sigDataUrl) {
      try {
        doc.addImage(sigDataUrl, getImageFormat(sigDataUrl), sigX, y - 8, 30, 11);
        drewImage = true;
      } catch {
        // Corrupt/unsupported image data - fall back to a blank signature line rather than failing the whole PDF.
      }
    }
    if (!drewImage) {
      doc.setFont('helvetica', 'normal');
      doc.text(BLANK, sigX, y);
    }
    y += 6;
    labelled('Date', formatDate(signedDate) || '____ / ____ / ______', y);
    y += 6;
    labelled('Company Stamp', '', y);
  };

  renderSignatureColumn(
    leftX,
    'FOR CLOUDSCREEN / PROVIDER',
    contract.provider_company_name,
    contract.provider_rep_name,
    contract.provider_rep_position,
    providerSigDataUrl,
    contract.provider_signed_date
  );
  renderSignatureColumn(
    rightX,
    'FOR ADVERTISER / CLIENT',
    contract.client_company_name,
    contract.client_rep_name,
    contract.client_rep_position,
    clientSigDataUrl,
    contract.client_signed_date
  );

  // ── Footer: brand tagline, orange rule, page numbers ─────────────────
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    doc.setDrawColor(...BRAND);
    doc.setLineWidth(0.5);
    doc.line(PAGE_MARGIN, cursor.pageHeight - 13, cursor.pageWidth - PAGE_MARGIN, cursor.pageHeight - 13);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(120, 120, 120);
    doc.text('CloudScreen is a brand of Cloudnet Software Co., Ltd.', PAGE_MARGIN, cursor.pageHeight - 8);

    doc.setFontSize(8);
    doc.setTextColor(...BRAND_DARK);
    doc.text(
      `${contract.agreement_no || ''}  •  Page ${i} of ${pageCount}`,
      cursor.pageWidth - PAGE_MARGIN,
      cursor.pageHeight - 8,
      { align: 'right' }
    );
    doc.setTextColor(...TEXT_DARK);
  }

  doc.save(`Contract-${contract.agreement_no || 'draft'}.pdf`);
};
